import { ArrowLeft } from "lucide-react";
import { Link, Outlet, useMatch, useOutletContext } from "react-router-dom";
import { AlertsControl, AlertsHint } from "./AlertsControl";
import { errorStatus, notifyErrorText } from "./api";
import { AccountEmail, SignOutButton, useNotifySignOut, useSignedInEmail } from "./NotifyAccount";
import { useNotifyMe, useRefetchMeOnForbidden } from "./queries";
import { canReadCases, pickSite } from "./site";
import type { NotifyMe } from "./types";
import { useNotifyDocument } from "./useNotifyDocument";
import { usePushAlerts } from "./usePushAlerts";

/** What every Notify page gets from the layout. */
export interface NotifyOutlet {
  me: NotifyMe;
}

export const useNotifyOutlet = () => useOutletContext<NotifyOutlet>();

/** On every Notify page, always: a neutral strip with a faint diagonal hatch, so
 * the page reads as "not real" at a glance. No hue (signal means active). */
function SimulationBanner() {
  return (
    <div
      role="note"
      className="flex min-h-7 items-center border-b border-border-strong bg-bg-2 px-4 py-[5px]"
      style={{
        backgroundImage:
          "repeating-linear-gradient(-45deg, var(--color-border-strong) 0 1px, transparent 1px 8px), linear-gradient(var(--color-surface-2), var(--color-surface-2))",
      }}
    >
      <span className="whitespace-nowrap bg-bg-2 px-1.5 py-px font-mono text-[10.5px] font-medium tracking-[0.14em] text-fg">
        SIMULATION · NOT FOR CLINICAL USE
      </span>
    </div>
  );
}

/** Takes the header's free space (pushing the account and alerts to the end) and
 * truncates its title rather than run under them on a phone. */
function Brand() {
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <img
        src="/images/a2z-logo-linear-dark.png"
        alt="a2z"
        className="block h-[18px] w-auto shrink-0"
      />
      <span className="truncate text-[13px] font-semibold">
        a2z Notify <span className="font-normal text-fg-dim">(simulation)</span>
      </span>
    </span>
  );
}

/** Why the caller has no Notify site to show, with the backend's reason, and a
 * way out for a wrong account (an installed app has no other sign out). */
function NoSite({
  error,
  email,
  onSignOut,
}: {
  error: unknown;
  email?: string;
  onSignOut: () => void;
}) {
  const status = errorStatus(error);
  return (
    <div role="alert" className="grid gap-1 p-6 text-sm text-fg-muted">
      <p className="font-semibold text-fg">
        {error
          ? status === 403
            ? notifyErrorText(error)
            : `Could not load Notify: ${notifyErrorText(error)}`
          : "You are not in the Notify directory"}
      </p>
      <p>Ask the Notify admin to add you.</p>
      <p className="mt-2">
        {email && <>Signed in as {email}. </>}
        Wrong account?{" "}
        <button
          type="button"
          className="font-medium text-fg underline underline-offset-2"
          onClick={onSignOut}
        >
          Sign out
        </button>
      </p>
    </div>
  );
}

/**
 * The installed Notify app's frame (/notify/*): the simulation banner, a header
 * with the signed-in account (Sign out) and the alerts control, and the page. The caller's site and roles come from
 * `GET /me`; the alerts control uses the first site. web-demo's Navbar and
 * Footer are hidden here (App.tsx). Runs only in notify/index.html: from
 * web-demo's index.html the page reloads into it first (useNotifyDocument).
 */
export default function NotifyLayout() {
  if (useNotifyDocument()) return <p className="p-6 text-sm text-fg-dim">Loading…</p>;
  return <NotifyFrame />;
}

function NotifyFrame() {
  const me = useNotifyMe();
  const site = pickSite(me.data);
  const refetchMeOnForbidden = useRefetchMeOnForbidden();
  const alerts = usePushAlerts(site?.site_id ?? null, refetchMeOnForbidden);
  const email = useSignedInEmail();
  const signOut = useNotifySignOut(alerts);
  const onSignOut = () => void signOut();
  const onCase = useMatch("/notify/cases/*");
  return (
    <div className="flex min-h-full flex-col bg-bg text-fg">
      <SimulationBanner />
      {/* Two rows on a phone (UI-01): back + brand + Sign out, then the email and
          the alerts control. One row from sm, ordered email, Sign out, alerts (the
          second row's box is display: contents there). One Sign out in the DOM. */}
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-border bg-bg-2/75 px-4 py-2 sm:min-h-12 sm:flex-nowrap sm:py-0">
        {onCase && site && canReadCases(site) && (
          <Link
            to="/notify/"
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[13px] text-fg-muted hover:text-fg"
          >
            <ArrowLeft aria-hidden className="size-3.5" />
            Cases
          </Link>
        )}
        <Brand />
        <SignOutButton onSignOut={onSignOut} className="shrink-0 sm:order-2" />
        {(email || site) && (
          <div className="flex min-w-0 basis-full items-center gap-3 sm:contents">
            <AccountEmail
              email={email}
              className="flex-1 sm:order-1 sm:max-w-[16rem] sm:flex-none"
            />
            {site && (
              <div className="ml-auto flex shrink-0 items-center sm:order-3 sm:ml-0">
                <AlertsControl alerts={alerts} />
              </div>
            )}
          </div>
        )}
      </header>
      <AlertsHint alerts={alerts} />
      {me.isLoading ? (
        <p className="p-6 text-sm text-fg-dim">Loading…</p>
      ) : !site || !me.data ? (
        <NoSite error={me.error} email={email} onSignOut={onSignOut} />
      ) : (
        <Outlet context={{ me: me.data } satisfies NotifyOutlet} />
      )}
    </div>
  );
}
